import { type SubmitEvent as ReactSubmitEvent, type FormEvent, memo, useState } from "react";
import "Components/ConfigLayout/Event/MotionEventSettings/MotionEventSettings.less";
import { Card, type SelectOption, Select, SettingsLayout, Switch } from "@clintonelec/react-storybook";
import { useAppDispatch, useAppSelector } from "Data/Redux/Store";
import { selectMotionEvent, setMotionEventAction } from "Data/Redux/Slices/Settings/Event/Motion";
import { produce } from "immer";
import ActionButtons from "Components/ConfigLayout/ActionButtons";
import { isEqual } from "lodash";

const intervalSelectOptions: SelectOption[] = [ 0, 5, 10, 15, 20, 25, 30 ].map(value => ({
	label: value === 0 ? "Off" : `${ value }`,
	value
}));

type MotionEventForm = {
	eventName: HTMLInputElement;
	alarmOut: HTMLInputElement;
	snapshot: HTMLInputElement;
	whiteLight: HTMLInputElement;
	redBlueLight: HTMLInputElement;
	audioAlarm: HTMLInputElement;
	recording: HTMLInputElement;
	email: HTMLInputElement;
	ftp: HTMLInputElement;
} & HTMLFormElement;

function MotionEventSettings() {
	const dispatch = useAppDispatch();
	const motionEventSettings = useAppSelector(selectMotionEvent);
	const [ localMotionEventSettings, setLocalMotionEventSettings ] = useState(motionEventSettings);
	const setMotionEvent = () => dispatch(setMotionEventAction(localMotionEventSettings));
	const disabled = isEqual(localMotionEventSettings, motionEventSettings);
	const {
		interval, alarmOut, snapshot, whiteLight, redBlueLight, audioAlarm, recording, email, ftp
	} = localMotionEventSettings;

	const handleAlarmOutOnChange = (newEnabled: boolean) => {
		setLocalMotionEventSettings(produce(localMotionEventSettings, draft => {
			draft.alarmOut = newEnabled;
		}));
	};

	const handleSnapshotOnChange = (newEnabled: boolean) => {
		setLocalMotionEventSettings(produce(localMotionEventSettings, draft => {
			draft.snapshot = newEnabled;
		}));
	};

	const handleWhiteLightOnChange = (newEnabled: boolean) => {
		setLocalMotionEventSettings(produce(localMotionEventSettings, draft => {
			draft.whiteLight = newEnabled;
		}));
	};

	const handleRedBlueLightOnChange = (newEnabled: boolean) => {
		setLocalMotionEventSettings(produce(localMotionEventSettings, draft => {
			draft.redBlueLight = newEnabled;
		}));
	};

	const handleAudioAlarmOnChange = (newEnabled: boolean) => {
		setLocalMotionEventSettings(produce(localMotionEventSettings, draft => {
			draft.audioAlarm = newEnabled;
		}));
	};

	const handleRecordingOnChange = (newEnabled: boolean) => {
		setLocalMotionEventSettings(produce(localMotionEventSettings, draft => {
			draft.recording = newEnabled;
		}));
	};

	const handleEmailOnChange = (newEnabled: boolean) => {
		setLocalMotionEventSettings(produce(localMotionEventSettings, draft => {
			draft.email = newEnabled;
		}));
	};

	const handleFtpChange = (newEnabled: boolean) => {
		setLocalMotionEventSettings(produce(localMotionEventSettings, draft => {
			draft.ftp = newEnabled;
		}));
	};

	const handleFormSubmit = (event: ReactSubmitEvent<MotionEventForm>) => {
		event.preventDefault();

		if (event.currentTarget?.checkValidity()) {
			setMotionEvent();
		}
	};

	const handleFormReset = (event: FormEvent<MotionEventForm>) => {
		event.preventDefault();

		setLocalMotionEventSettings(motionEventSettings);
	};

	const handleIntervalSelect = (newInterval: string | null) => {
		if (!newInterval) {
			return;
		}

		const newLocalState = produce(localMotionEventSettings, draft => {
			draft.interval = +newInterval;
		});

		setLocalMotionEventSettings(newLocalState);
	};

	return (
		<SettingsLayout.Form
			className="motion-event-settings"
			noValidate
			onReset={ handleFormReset }
			onSubmit={ handleFormSubmit }
		>
			<SettingsLayout.ScrollableContainer>
				<Card size="medium" title="Motion Event Settings">
					<div className="motion-event-content">
						<div className="form-row">
							<span>Ignoring Interval</span>
							<Select
								allowClear={ false }
								className="form-select"
								onSelect={ handleIntervalSelect }
								options={ intervalSelectOptions }
								value={ interval }
							/>
						</div>
						<div className="form-row">
							<span>Alarm Out</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAlarmOutOnChange } checked={ alarmOut } />
							</div>
						</div>
						<div className="form-row">
							<span>Snapshot</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleSnapshotOnChange } checked={ snapshot } />
							</div>
						</div>
						<div className="form-row">
							<span>White Light</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleWhiteLightOnChange } checked={ whiteLight } />
							</div>
						</div>
						<div className="form-row">
							<span>Red & Blue Light</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleRedBlueLightOnChange } checked={ redBlueLight } />
							</div>
						</div>
						<div className="form-row">
							<span>Audio Alarm</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleAudioAlarmOnChange } checked={ audioAlarm } />
							</div>
						</div>
						<div className="form-row">
							<span>Recording</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleRecordingOnChange } checked={ recording } />
							</div>
						</div>
						<div className="form-row">
							<span>E-Mail</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleEmailOnChange } checked={ email } />
							</div>
						</div>
						<div className="form-row">
							<span>FTP</span>
							<div className="form-switch-wrapper">
								<Switch onChange={ handleFtpChange } checked={ ftp } />
							</div>
						</div>
					</div>
				</Card>
			</SettingsLayout.ScrollableContainer>
			<ActionButtons disabled={ disabled } />
		</SettingsLayout.Form>
	);
}

export default memo(MotionEventSettings);
